import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Category, SubCategory, Characteristic, Document } from '../../types/catalog';
import { fetchCategories, fetchSubcategoriesByCategory } from '../../api/catalog';
import { getAssetUrl } from '../../config';
import ProductActions from '../../components/ProductActions';

const DownloadIcon = () => (
  <svg 
    className="w-5 h-5 inline-block ml-2" 
    fill="currentColor" 
    viewBox="0 0 20 20"
  >
    <path 
      fillRule="evenodd" 
      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" 
      clipRule="evenodd"
    />
  </svg>
);

const VideoIcon = () => (
  <svg 
    className="w-5 h-5 inline-block ml-2" 
    fill="currentColor" 
    viewBox="0 0 20 20"
  >
    <path 
      fillRule="evenodd" 
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" 
      clipRule="evenodd"
    />
  </svg>
);

const LoadingSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[...Array(6)].map((_, index) => (
      <div key={index} className="card animate-pulse">
        <div className="h-[300px] bg-gray-200 rounded-md mb-4"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-full"></div>
      </div>
    ))}
  </div>
);

const CatalogPage = () => {
  const { categoryId, subcategoryId } = useParams<{ categoryId: string; subcategoryId: string }>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        const data = await fetchCategories();
        setCategories(data);
        setError(null);
      } catch (err) {
        setError('Ошибка загрузки категорий');
        console.error('Error loading categories:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    const loadSubcategories = async () => {
      if (!categoryId) return;
      
      try {
        setLoading(true);
        const data = await fetchSubcategoriesByCategory(categoryId);
        setSubcategories(data);
        setError(null);
      } catch (err) {
        setError('Ошибка загрузки подкатегорий');
        console.error('Error loading subcategories:', err);
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) {
      loadSubcategories();
    }
  }, [categoryId]);

  if (categoryId) {
    const category = categories.find(c => c.id === categoryId);
    
    if (!category) {
      return (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-6">Категория не найдена</h1>
          <Link to="/catalog" className="text-primary hover:underline">
            ← Вернуться к каталогу
          </Link>
        </div>
      );
    }

    if (subcategoryId) {
      const subcategory = subcategories.find(s => s.id === subcategoryId);

      if (!subcategory) {
        return (
          <div className="max-w-7xl mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-6">Подкатегория не найдена</h1>
            <Link to={`/catalog/${categoryId}`} className="text-primary hover:underline">
              ← Вернуться к категории
            </Link>
          </div>
        );
      }

      return (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-6 space-y-2">
            <Link to="/catalog" className="text-primary hover:underline block">
              ← Вернуться к каталогу
            </Link>
            <Link to={`/catalog/${categoryId}`} className="text-primary hover:underline block">
              ← Вернуться к категории {category.name}
            </Link>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Image Section */}
              <div className="h-[700px] rounded-md overflow-hidden flex items-center justify-center">
                {subcategory.image ? (
                  <img
                    src={getAssetUrl(subcategory.image)}
                    alt={subcategory.name}
                    className="max-h-full max-w-full object-contain p-4"
                  />
                ) : (
                  <span className="text-gray-400">Нет изображения</span>
                )}
              </div>

              {/* Info Section */}
              <div className="space-y-6">
                <h1 className="text-3xl font-bold">{subcategory.name}</h1>
                
                {subcategory.manufacturer && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-700">Производитель</h2>
                    <p className="text-gray-600">{subcategory.manufacturer}</p>
                  </div>
                )}

                {subcategory.fullDescription && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-700">Описание</h2>
                    <p className="text-gray-600">{subcategory.fullDescription}</p>
                  </div>
                )}

                <ProductActions product={subcategory} />

                <div className="pt-6 flex flex-col gap-5">
                  <Link to="/callback" className="btn-primary block text-center">
                    Получить консультацию
                  </Link>
                  <Link to="/price-lists" className="btn-secondary block text-center">
                    Скачать прайслисты
                  </Link>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Technical Characteristics */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Характеристики</h2>
                <div className="space-y-2">
                  {subcategory.characteristics && subcategory.characteristics.length > 0 ? (
                    subcategory.characteristics.map((char: Characteristic, index: number) => (
                      <div key={index} className="flex items-center">
                        {char.type === 'file' ? (
                          <a
                            href={getAssetUrl(char.fileUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="text-primary hover:text-primary-dark flex items-center"
                          >
                            {char.name}
                            <DownloadIcon />
                          </a>
                        ) : char.type === 'video' ? (
                          <a
                            href={char.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary-dark flex items-center"
                          >
                            {char.name}
                            <VideoIcon />
                          </a>
                        ) : (
                          <div className="flex justify-between w-full">
                            <span className="text-gray-600">{char.name}</span>
                            <span className="font-medium">{char.value}</span>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-600">Технические характеристики будут добавлены позже</p>
                  )}
                </div>
              </div>

              {/* Documentation */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Документация</h2>
                <div className="space-y-2">
                  {subcategory.documents && subcategory.documents.length > 0 ? (
                    subcategory.documents.map((doc: Document, index: number) => (
                      <div key={index} className="flex items-center">
                        {doc.type === 'file' ? (
                          <a
                            href={getAssetUrl(doc.fileUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="text-primary hover:text-primary-dark flex items-center"
                          >
                            {doc.name}
                            <DownloadIcon />
                          </a>
                        ) : (
                          <a
                            href={doc.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary-dark flex items-center"
                          >
                            {doc.name}
                            <VideoIcon />
                          </a>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-600">Документация будет добавлена позже</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Show subcategories for the selected category
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/catalog" className="text-primary hover:underline">
            ← Вернуться к каталогу
          </Link>
        </div>
        
        <h1 className="text-3xl font-bold mb-6">{category.name}</h1>
        {category.description && (
          <p className="text-gray-600 mb-8">{category.description}</p>
        )}

        {loading || error ? (
          <LoadingSkeleton />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subcategories.map(subcategory => (
              <Link
                key={subcategory.id}
                to={`/catalog/${categoryId}/${subcategory.id}`}
                className="card group hover:shadow-lg transition-all"
              >
                <div className="h-[300px] rounded-md overflow-hidden flex items-center justify-center mb-4">
                  {subcategory.image ? (
                    <img
                      src={getAssetUrl(subcategory.image)}
                      alt={subcategory.name}
                      className="max-h-full max-w-full object-contain p-4"
                    />
                  ) : (
                    <span className="text-gray-400">Нет изображения</span>
                  )}
                </div>
                <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                  {subcategory.name}
                </h2>
                {subcategory.shortDescription && (
                  <p className="text-gray-600 mb-2">{subcategory.shortDescription}</p>
                )}
                <div className="mt-4">
                  <ProductActions product={subcategory} compact />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Show all categories
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Каталог продукции</h1>
      {loading || error ? (
        <LoadingSkeleton />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map(category => (
            <Link
              key={category.id}
              to={`/catalog/${category.id}`}
              className="card group hover:shadow-lg transition-all"
            >
              <div className="h-[300px] rounded-md overflow-hidden flex items-center justify-center mb-4">
                {category.image ? (
                  <img
                    src={getAssetUrl(category.image)}
                    alt={category.name}
                    className="max-h-full max-w-full object-contain p-4"
                  />
                ) : (
                  <span className="text-gray-400">Нет изображения</span>
                )}
              </div>
              <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                {category.name}
              </h2>
              {category.description && (
                <p className="text-gray-600">{category.description}</p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default CatalogPage; 